import APICall from "../../utils/APICalls.js";
import responseModel from "../../models/Generic/responseModel";

// Helper function to parse name from description
const parseNameFromDescription = (description) => {
  if (!description) return { firstName: "", lastName: "" };

  const nameParts = description.trim().split(" ");
  if (nameParts.length === 1) {
    return { firstName: nameParts[0], lastName: "" };
  }

  const firstName = nameParts[0];
  const lastName = nameParts.slice(1).join(" ");
  return { firstName, lastName };
};

// Helper function to transform API user to internal format
const transformUser = (apiUser, index) => {
  const { firstName, lastName } = parseNameFromDescription(apiUser.description);

  return {
    id: index + 1, // Use index as ID since API doesn't provide one
    email: apiUser.applicationUser,
    firstName: firstName,
    lastName: lastName,
    description: apiUser.description,
    role: apiUser.role.replace(/[{}]/g, ""), // Remove curly braces from role
    isSuspended: apiUser.isSuspended,
    fullName: apiUser.description,
    statusText: apiUser.isSuspended ? "Suspended" : "Active",
    lastLogin: null,
    createdDate: null,
    insertTs: null,
    insertUser: "",
    updateTs: null,
    updateUser: "",
  };
};

const UserServiceClient = {
  getPaginated: async function (pageModel) {
    const result = await APICall.get(`User/ApplicationUsers`);

    if (result && result.succeeded && result.object) {
      let users = result.object.map((user, index) =>
        transformUser(user, index),
      );

      // Apply filters if any
      if (pageModel.filters && pageModel.filters.length > 0) {
        pageModel.filters.forEach((filter) => {
          const { member, value } = filter;
          if (value && value.trim() !== "") {
            users = users.filter((user) => {
              let fieldValue = user[member];

              // Handle nested properties for filtering
              if (member === "statusText") {
                fieldValue = user.isSuspended ? "Suspended" : "Active";
              }

              if (typeof fieldValue === "string") {
                return fieldValue.toLowerCase().includes(value.toLowerCase());
              } else if (typeof fieldValue === "boolean") {
                return fieldValue.toString() === value.toLowerCase();
              }
              return false;
            });
          }
        });
      }

      // Apply sorting
      if (pageModel.sorts && pageModel.sorts.length > 0) {
        const sort = pageModel.sorts[0];
        users.sort((a, b) => {
          let aVal = a[sort.member] || "";
          let bVal = b[sort.member] || "";

          // Handle special fields
          if (sort.member === "fullName") {
            aVal = a.description;
            bVal = b.description;
          } else if (sort.member === "statusText") {
            aVal = a.isSuspended ? "Suspended" : "Active";
            bVal = b.isSuspended ? "Suspended" : "Active";
          }

          if (sort.direction === 1) {
            // ASC
            return aVal > bVal ? 1 : -1;
          } else {
            // DESC
            return aVal < bVal ? 1 : -1;
          }
        });
      } else {
        // Default sort by email
        users.sort((a, b) => a.email.localeCompare(b.email));
      }

      // Apply pagination
      const startIndex = pageModel.skip || 0;
      const endIndex = startIndex + (pageModel.take || 10);
      const paginatedUsers = users.slice(startIndex, endIndex);

      const paginatedResult = {
        items: paginatedUsers,
        total: users.length,
        take: pageModel.take || 10,
      };

      return responseModel.responseModelOk(paginatedResult);
    }

    return result;
  },

  getAll: async function () {
    const result = await APICall.get(`User/ApplicationUsers`);

    if (result && result.succeeded && result.object) {
      const users = result.object.map((user, index) =>
        transformUser(user, index),
      );
      return responseModel.responseModelOk(users);
    }

    return result;
  },

  get: async function (email) {
    const result = await APICall.get(
      `User/ApplicationUsers/${encodeURIComponent(email)}`,
    );

    if (result && result.succeeded && result.object) {
      const user = transformUser(result.object, 0);
      return responseModel.responseModelOk(user);
    }

    return result;
  },

  getById: async function (userId) {
    const result = await APICall.get(
      `User/ApplicationUsers/${encodeURIComponent(userId)}`,
    );

    if (result && result.succeeded && result.object) {
      const user = transformUser(result.object, 0);
      return responseModel.responseModelOk(user);
    }

    return result;
  },

  add: async function (user) {
    const userPayload = {
      applicationUser: user.email,
      description: user.description,
      role: `{${user.role}}`, // Add curly braces as per API format
      password: user.password || "TempPassword123!", // Default password if not provided
    };

    const result = await APICall.post(`User/ApplicationUsers`, userPayload);

    if (result && result.succeeded) {
      return responseModel.responseModelOk(result.object);
    }

    return result;
  },

  update: async function (user) {
    const userPayload = {
      description: user.description,
      role: `{${user.role}}`, // Add curly braces as per API format
      isSuspended: user.isSuspended || false,
    };

    const result = await APICall.put(
      `User/ApplicationUsers/${encodeURIComponent(user.email)}`,
      userPayload,
    );

    if (result && result.succeeded) {
      return responseModel.responseModelOk(result.object);
    }

    return result;
  },

  delete: async function (email) {
    const result = await APICall.delete(`User/ApplicationUser/${email}`);

    if (result && result.succeeded) {
      return responseModel.responseModelOk(true);
    }

    return result;
  },

  setPassword: async function (userId, newPassword) {
    const payload = {
      newPassword: newPassword,
    };

    const result = await APICall.put(
      `User/ApplicationUsers/${encodeURIComponent(userId)}/Password`,
      payload,
    );

    if (result && result.succeeded) {
      return responseModel.responseModelOk(true);
    }
    return result;
  },

  getRoles: async function () {
    // Extract unique roles from users
    const result = await APICall.get(`User/ApplicationUsers`);

    if (result && result.succeeded && result.object) {
      const uniqueRoles = [
        ...new Set(result.object.map((user) => user.role.replace(/[{}]/g, ""))),
      ];
      const roles = uniqueRoles.map((role) => ({
        id: role,
        description: role,
      }));
      return responseModel.responseModelOk(roles);
    }
    return result;
  },

  getGrouped: async function (propertyName) {
    const result = await APICall.get(`User/ApplicationUsers`);

    if (result && result.succeeded && result.object) {
      const users = result.object.map((user, index) =>
        transformUser(user, index),
      );
      const grouped = {};

      users.forEach((user) => {
        const value = user[propertyName];
        if (!grouped[value]) {
          grouped[value] = [];
        }
        grouped[value].push(user);
      });

      return responseModel.responseModelOk(grouped);
    }
    return result;
  },

  forgotPassword: async function (email) {
    return await APICall.post(`User/ForgotPassword`, { email });
  },

  validateOtp: async function (email, otpCode) {
    return await APICall.post(`User/ValidateOtp`, { email, otpCode });
  },

  resetPassword: async function (email, otpCode, newPassword, confirmPassword) {
    return await APICall.post(`User/ResetPassword`, {
      email,
      otpCode,
      newPassword,
      confirmPassword,
    });
  },
};

export default UserServiceClient;
